// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { StyleSheet, Text, View } from 'react-native';

import { formatHoleDistance, type DistanceUnit } from '@/features/units/distance';
import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';

import { lengthSourceLabel } from './lengthSourceLabel';
import { buildHoleLengthTable } from './holeLengthRows';
import type { CourseHole } from './useCourseHoles';

type HoleLengthTableProps = {
  hole: CourseHole | null;
  distanceUnit: DistanceUnit;
};

/**
 * Every stored length for one hole: a row per tee, a column per source that measured it.
 *
 * Admin-only, and shown where the geometry is — this is the screen where a tee marked on the
 * wrong box is found, and the finding is a comparison between sources. The single number in the
 * hole's subtitle is what a reader is served; this is what it was chosen from.
 *
 * Nothing here is editable. Correcting a card length is the course-config form's job and
 * correcting our own measurement means moving the marker on the map above.
 */
export function HoleLengthTable({ hole, distanceUnit }: HoleLengthTableProps) {
  const { t } = useI18n();
  const table = buildHoleLengthTable(hole);
  if (!table) return null;

  const sourceLabel = (source: string) => lengthSourceLabel(source, t);

  return (
    <View style={styles.table} testID="course-hole-map-length-table">
      <Text style={styles.caption}>{t('courseHoleMap.lengthTableTitle')}</Text>
      <View style={styles.row}>
        <Text style={[styles.cell, styles.teeCell, styles.headingText]}>
          {t('courseHoleMap.lengthTableTee')}
        </Text>
        {table.sources.map((source) => (
          <Text
            key={source}
            style={[styles.cell, styles.headingText]}
            testID={`course-hole-map-length-heading-${source}`}
          >
            {sourceLabel(source)}
          </Text>
        ))}
      </View>
      {table.rows.map((row) => (
        <View key={row.key} style={styles.row} testID={`course-hole-map-length-row-${row.key}`}>
          <Text style={[styles.cell, styles.teeCell, styles.teeText]}>
            {row.teeName ?? t('courseHoleMap.lengthTableHole')}
          </Text>
          {row.cells.map((length, index) => (
            <Text
              key={table.sources[index]}
              style={styles.cell}
              testID={`course-hole-map-length-cell-${row.key}-${table.sources[index]}`}
            >
              {/* An em dash, not a zero and not a blank: this source was asked and has nothing
                  to say about this tee, which is a different fact from a length of nought. */}
              {length === null ? '—' : formatHoleDistance(length, distanceUnit)}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  table: {
    borderWidth: 1,
    borderColor: appColors.border.subtle,
    borderRadius: 10,
    padding: 10,
    gap: 4,
  },
  caption: { color: appColors.text.primary, fontSize: 13, fontWeight: '700' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  // Every column the same width bar the tee's, so the numbers line up down the column and a
  // cell that disagrees with the one above it is visible without reading either.
  cell: {
    flex: 1,
    color: appColors.text.secondary,
    fontSize: 12,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
  teeCell: { flex: 1.4, textAlign: 'left' },
  teeText: { color: appColors.text.primary, fontWeight: '600' },
  headingText: { color: appColors.text.muted, fontSize: 11, fontWeight: '600' },
});
